import { useEffect, useRef, useState } from 'react';
import { fetchLpChatMessages, sendLpChatMessage } from '../../services/api.js';

const createMessage = ({ id, text, author, ...rest }) => ({
  id: id || `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  text,
  author,
  createdAt: new Date().toISOString(),
  ...rest,
});

const FAKE_GREETING_DELAY_MS = 3000;
const FAKE_GREETING_TEXT = 'Oiii 👋\nVocê está falando com Fin. Como posso te ajudar?';

const mapLpMessages = (items = []) => {
  const mapped = items.map((msg) => ({
    id: msg.id || `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    text: msg.message || '',
    author: msg.from_bot || msg.from_me ? 'agent' : 'user',
    createdAt: msg.date || new Date().toISOString(),
  }));

  return mapped.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
};

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

// Quebra um trecho de texto, transformando URLs em links clicáveis.
const linkifyText = (text, keyPrefix) => {
  const parts = [];
  let lastIndex = 0;
  let match;

  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // remove pontuação final que normalmente não faz parte da URL
    let url = match[0];
    let trailing = '';
    const trailingMatch = url.match(/[.,;:!?)\]}>"']+$/);
    if (trailingMatch) {
      trailing = trailingMatch[0];
      url = url.slice(0, url.length - trailing.length);
    }

    parts.push(
      <a
        key={`${keyPrefix}-link-${match.index}`}
        className="support-chat__link"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {url}
      </a>
    );
    if (trailing) parts.push(trailing);

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const formatMessageText = (text, { linkify = true } = {}) => {
  if (typeof text !== 'string') return text;

  URL_REGEX.lastIndex = 0;
  const hasUrl = linkify && URL_REGEX.test(text);
  if (!text.includes('**') && !hasUrl) return text;

  // Quando linkify está desativado (ex.: preview dentro de um <button>),
  // mantemos apenas a formatação de negrito.
  const renderSegment = (segment, keyPrefix) =>
    linkify ? linkifyText(segment, keyPrefix) : [segment];

  const parts = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...renderSegment(text.slice(lastIndex, match.index), `b${match.index}`));
    }
    parts.push(
      <strong key={`bold-${match.index}`}>
        {renderSegment(match[1], `s${match.index}`)}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(...renderSegment(text.slice(lastIndex), `t${lastIndex}`));
  }

  return parts;
};

const SupportChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isPreviewDismissed, setIsPreviewDismissed] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  const [clientId, setClientId] = useState(() => {
    try {
      return localStorage.getItem('lp_chat_client_id') || '';
    } catch {
      return '';
    }
  });
  const lastMessageCountRef = useRef(0);
  const isOpenRef = useRef(isOpen);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesRef = useRef(messages);
  const fakeGreetingRef = useRef(false);

  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!clientId) return;
    try {
      localStorage.setItem('lp_chat_client_id', clientId);
    } catch {
    }
  }, [clientId]);

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      if (!clientId) return;
      try {
        const data = await fetchLpChatMessages(clientId);
        const items = Array.isArray(data?.mensagens) ? data.mensagens : [];
        const mapped = mapLpMessages(items);

        if (!isActive) return;

        if (!isOpenRef.current && mapped.length > lastMessageCountRef.current) {
          const newMessages = mapped.slice(lastMessageCountRef.current);
          const newUnread = newMessages.filter((msg) => msg.author === 'agent').length;
          if (newUnread) {
            setIsPreviewDismissed(false);
            setUnreadCount((prev) => prev + newUnread);
          }
        }

        setMessages((prev) => {
          if (mapped.length > 0) return mapped;
          const hasFake = prev.some((msg) => msg.isFake);
          return hasFake ? prev : mapped;
        });
      } catch {
      } finally {
        if (isActive) {
          setHasLoadedHistory(true);
        }
      }
    };

    load();
    const intervalId = clientId ? setInterval(load, 5000) : null;

    return () => {
      isActive = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      setHasLoadedHistory(true);
    }
  }, [clientId]);

  useEffect(() => {
    const previousCount = lastMessageCountRef.current;
    lastMessageCountRef.current = messages.length;

    if (isOpen && messages.length > previousCount) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!hasLoadedHistory || fakeGreetingRef.current) return;

    const timeoutId = setTimeout(() => {
      if (fakeGreetingRef.current || messagesRef.current.length > 0) return;

      fakeGreetingRef.current = true;
      setMessages((prev) => [
        ...prev,
        createMessage({
          text: FAKE_GREETING_TEXT,
          author: 'agent',
          isFake: true,
        }),
      ]);
      if (!isOpenRef.current) {
        setIsPreviewDismissed(false);
        setUnreadCount((prev) => prev + 1);
      }
    }, FAKE_GREETING_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [hasLoadedHistory]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };
  const handlePreviewDismiss = () => {
    setIsPreviewDismissed(true);
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || isSending) return;

    setMessages((prev) => [
      ...prev,
      createMessage({
        text,
        author: 'user',
      }),
    ]);
    setDraft('');
    setIsSending(true);
    try {
      const data = await sendLpChatMessage({
        clientId: clientId || undefined,
        message: text,
      });
      if (data?.clientId) {
        setClientId(String(data.clientId));
      }
      if (data?.response) {
        setMessages((prev) => [
          ...prev,
          createMessage({
            text: data.response,
            author: 'agent',
          }),
        ]);
      }
    } catch {
    } finally {
      setIsSending(false);
    }
  };

  const lastMessage = messages[messages.length - 1];
  const showPreview = !isOpen && unreadCount > 0 && lastMessage && !isPreviewDismissed;

  return (
    <div className={`support-chat${isOpen ? ' is-open' : ''}`}>
      {showPreview && (
        <div className="support-chat__preview">
          <button type="button" className="support-chat__preview-main" onClick={handleToggle}>
            <div className="support-chat__preview-avatar">F</div>
            <div className="support-chat__preview-body">
              <div className="support-chat__preview-title">Fin</div>
              <div className="support-chat__preview-text">
                {formatMessageText(lastMessage.text, { linkify: false })}
              </div>
              <div className="support-chat__preview-meta">agora</div>
            </div>
          </button>
          <button
            type="button"
            className="support-chat__preview-close"
            onClick={handlePreviewDismiss}
            aria-label="Fechar preview do chat"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      {isOpen && (
        <div
          className="support-chat__panel"
          role="dialog"
          aria-label="Chat de suporte"
          id="support-chat-panel"
        >
          <div className="support-chat__header">
            <div className="support-chat__profile">
              <div className="support-chat__avatar">F</div>
              <div>
                <div className="support-chat__name">Fin</div>
                <div className="support-chat__subtitle">A equipe também pode ajudar</div>
              </div>
            </div>
            <button type="button" className="support-chat__close" onClick={handleToggle} aria-label="Fechar chat">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className="support-chat__body">
            {messages.length === 0 ? (
              <div className="support-chat__empty">Ainda não há mensagens.</div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`support-chat__message support-chat__message--${message.author}`}
                >
                  <div className="support-chat__bubble">{formatMessageText(message.text)}</div>
                  <div className="support-chat__meta">
                    {message.author === 'agent' ? 'Fin' : 'Você'} &bull; agora
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="support-chat__composer" onSubmit={handleSend}>
            <input
              ref={inputRef}
              id="support-message"
              name="supportMessage"
              type="text"
              className="support-chat__input"
              placeholder="Digite sua mensagem..."
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              disabled={isSending}
            />
            <button
              type="submit"
              className="support-chat__send"
              disabled={!draft.trim() || isSending}
              aria-label="Enviar mensagem"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path
                  d="M3.4 20.6L21 12 3.4 3.4 3 10.2l10.8 1.8L3 13.8z"
                />
              </svg>
            </button>
          </form>
        </div>
      )}

      <div className="support-chat__toggle-wrap">
        <button
          type="button"
          className="support-chat__toggle"
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-controls="support-chat-panel"
          aria-label={isOpen ? 'Fechar chat de suporte' : 'Abrir chat de suporte'}
        >
          <svg
            className="support-chat__toggle-icon"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 4h12a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H10l-4 4v-4H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <path
              d="M8 9h8M8 12h6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        {unreadCount > 0 && (
          <span className="support-chat__badge" aria-label="Mensagens não lidas">
            {unreadCount}
          </span>
        )}
      </div>
    </div>
  );
};

export default SupportChat;
